import { Button, Flex, Typography } from '@iheartjane/reefer';
import { useCallback } from 'react';

import { useNavigateBack } from '@jane/shared/hooks';

import { Modal } from '../../components';
import type {
  CartConfirmationLoaderType,
  RootLoaderType,
} from '../../data-access';
import { getLocalCartUuid, useRemoveCartProduct } from '../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../hooks';
import { useProductTracking } from '../../providers';

export const CartRemovalConfirmation = () => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const cartUuid = getLocalCartUuid();
  const { productId } =
    useTypedRouteLoaderData<CartConfirmationLoaderType>('cartConfirmation');

  const {
    inlineAds: { removeInlineAdId },
    cartToppers: { removeCartTopperId },
  } = useProductTracking();

  const { mutate: removeCartProduct, isLoading } = useRemoveCartProduct({});
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  const onRemoveProduct = useCallback(() => {
    removeCartProduct(
      {
        cart_uuid: cartUuid,
        product_id: productId,
      },
      {
        onSuccess: () => {
          removeCartTopperId(productId);
          removeInlineAdId(productId);
          goBack();
        },
      }
    );
  }, [
    cartUuid,
    goBack,
    productId,
    removeCartProduct,
    removeCartTopperId,
    removeInlineAdId,
  ]);

  return (
    <Modal
      appId="root"
      contentLabel="Remove product confirm"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content>
        <Typography mb={24} variant="title-bold">
          Remove this product?
        </Typography>
        <Typography mb={32}>
          Are you sure you want to remove this product from your bag?
        </Typography>
        <Flex flexDirection="row">
          <Button
            ariaLabel="cancel"
            full
            label={
              <Typography
                color="text-main"
                variant={`${isIpad ? 'body' : 'section'}-bold`}
              >
                Cancel
              </Typography>
            }
            mr={getKioskSpacing(24, 16)}
            onClick={() => goBack()}
            variant="tertiary"
            size={isIpad ? 'default' : 'large'}
          />
          <Button
            ariaLabel="remove product"
            full
            label={
              <Typography
                color="grays-white"
                variant={`${isIpad ? 'body' : 'section'}-bold`}
              >
                Remove
              </Typography>
            }
            disabled={isLoading}
            loading={isLoading}
            onClick={onRemoveProduct}
            variant="destructive"
            size={isIpad ? 'default' : 'large'}
          />
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
